import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import SupportChatComponent from 'src/app/pages/support-chat/support-chat.component';
import { SupportChatStore } from 'src/app/pages/support-chat/services/support-chat.store';
import { ZendeskService } from 'src/app/services/zendesk.service';
import { OffersCarouselComponent } from 'src/app/components/offers/offers-carousel.component';
import { AppUpdatesStore } from 'src/app/store/app-updates.store';
import { AuthStore } from 'src/app/store/auth.store';
import { SiteStore } from 'src/app/store/site.store';

const LAUNCHER_ROUTE = '/fund-account/crypto';

@Component({
  selector: 'app-support-chat-widget',
  standalone: true,
  imports: [CommonModule, SupportChatComponent, OffersCarouselComponent],
  templateUrl: './support-chat.component.html',
  styleUrls: ['./support-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupportChatWidgetComponent implements OnInit, OnDestroy {
  readonly store = inject(SupportChatStore);
  private readonly router = inject(Router);
  private readonly zendesk = inject(ZendeskService);
  private readonly appUpdatesStore = inject(AppUpdatesStore);
  private readonly authStore = inject(AuthStore);
  private readonly siteStore = inject(SiteStore);
  readonly visible = signal(false);
  readonly onLauncherRoute = signal(false);
  // Whether the "¿Necesitas ayuda?" hint bubble has been dismissed for the
  // current visit. Reset every time the user re-enters the launcher route.
  readonly hintDismissed = signal(false);
  // Whether the Zendesk "¿Necesitas ayuda?" hint bubble has been dismissed.
  readonly zendeskHintDismissed = signal(false);

  // Cleanup for the Zendesk 'open' subscription registered in ngOnInit.
  private zendeskOpenUnsub?: () => void;

  readonly showHint = computed(
    () =>
      this.onLauncherRoute() &&
      this.visible() &&
      !this.store.widgetOpen() &&
      !this.hintDismissed()
  );

  // Hint shown over the Zendesk launcher on every route where Zendesk is
  // visible (i.e. everywhere except our own launcher route).
  readonly showZendeskHint = computed(
    () =>
      !this.onLauncherRoute() &&
      this.visible() &&
      !this.zendeskHintDismissed()
  );

  // Desktop offers carousel, above the hint bubble. On mobile the offers go in
  // the ribbon under the navbar (app-offers-ribbon) instead.
  readonly showOffers = computed(
    () =>
      this.visible() &&
      !!this.authStore.user() &&
      !this.authStore.closingSession() &&
      this.siteStore.isNotMobile() &&
      !this.store.widgetOpen() &&
      this.appUpdatesStore.offers().length > 0
  );

  readonly showStack = computed(() => this.showOffers() || this.showHint() || this.showZendeskHint());

  ngOnInit(): void {
    setTimeout(() => this.visible.set(true), 3000);
    // Dismiss the Zendesk hint once the user actually opens the widget.
    this.zendeskOpenUnsub = this.zendesk.onOpen(() =>
      this.zendeskHintDismissed.set(true)
    );
    this.syncRouteState(this.router.url, true);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.syncRouteState(e.urlAfterRedirects, false));
  }

  ngOnDestroy(): void {
    this.zendeskOpenUnsub?.();
  }

  // Keep the launcher-route signal and the Zendesk visibility in sync with
  // the current URL. On the launcher route we hide Zendesk so it doesn't
  // overlap with our own floating chat; everywhere else we restore it. We
  // also reset the hint each time the user (re)enters the launcher route.
  private syncRouteState(url: string, initial: boolean): void {
    const onRoute = this.matchesLauncherRoute(url);
    const wasOnRoute = this.onLauncherRoute();
    this.onLauncherRoute.set(onRoute);
    if (onRoute) {
      this.zendesk.hide();
      if (initial || !wasOnRoute) this.hintDismissed.set(false);
      // Auto-open chat when coming from /crypto-contact redirect
      if (url.includes('openChat=true')) {
        this.visible.set(true);
        this.store.openWidget();
      }
    } else {
      this.zendesk.show();
    }
  }

  private matchesLauncherRoute(url: string): boolean {
    return url.split('?')[0].split('#')[0] === LAUNCHER_ROUTE;
  }

  toggle(): void {
    this.hintDismissed.set(true);
    this.store.toggleWidget();
  }

  close(): void {
    this.store.closeWidget();
  }

  // Close the floating chat when the user hits Esc while it's open. Bound
  // at the document level so the listener still fires even if focus is
  // inside an iframe-like child (composer textarea, etc.).
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.store.widgetOpen()) return;
    event.preventDefault();
    this.close();
  }

  dismissHint(): void {
    this.hintDismissed.set(true);
  }

  dismissZendeskHint(): void {
    this.zendeskHintDismissed.set(true);
  }

  // Open the Zendesk widget from the hint bubble and dismiss it.
  openZendeskHint(): void {
    this.zendeskHintDismissed.set(true);
    this.zendesk.openZendeskWidget();
  }
}
